import type { IAnyModelType } from "mobx-state-tree";
import { types, flow, getSnapshot, destroy } from "mobx-state-tree";

/**
 * @summary A base store to be composed with a migrated collection store
 * @param {MSTStore} store          - Migrated collection store
 * @param {MSTStore} modelStore     - Migrated model store used in the collection
 * @param {string} modelsIdentifier - Name of the array that holds the list of models
 * @return {Composed MSTStore}      - Returns a composition of this base store and the parsed store (Simulating inheritance)
 */
const BaseBackboneCollectionMigrationStore = <T extends IAnyModelType>(
    store: T,
    modelStore,
    modelsIdentifier
) => {
    const modelIdAttribute = modelStore.identifierAttribute;

    const model = {};

    const volatile = () => ({
        __collection: null
    });

    const views = (self) => ({
        getAsObject() {
            return getSnapshot(self.__getModels());
        },
        getAsJSON() {
            return JSON.stringify(self.__getModels());
        },
        getLength() {
            return self.__getModels()?.length;
        }
    });

    const actions = (self) => ({
        // Generic sort which sorts models by their unique id
        // This can be overriden by defining a bespoke sort in the combined store
        sort() {
            self[modelsIdentifier].sort((model1, model2) => {
                return `${model1[modelIdAttribute]}`.localeCompare(
                    model2[modelIdAttribute]
                );
            });
        },
        destroyChild(child) {
            destroy(child);
        },

        /**
         * @summary Resets the store with new data
         */
        reset(models) {
            self[modelsIdentifier] = models.map((model) => {
                return modelStore.create(model);
            });
        }
    });

    const asyncActions = (self) => ({
        fetch: flow(function* fetch() {
            yield new Promise(() => {
                return self.getAsObject();
            });
        })
    });

    const backboneUtilityActions = (self) => ({
        __syncRead: flow(function* __syncRead() {
            try {
                const response = yield self.fetch();
                return response;
            } catch (error) {
                console.error(error);
                return error;
            }
        }),

        __getModels() {
            return self[modelsIdentifier];
        },

        __getModelById(id, returnNode = false) {
            const model = self
                .__getModels()
                ?.find((model) => model[modelIdAttribute] === id);
            if (returnNode || !model) {
                return model;
            }
            return getSnapshot(model);
        },

        __reset(models) {
            self.reset(models);
            return self.__getModels();
        },

        __sort() {
            self.sort();
        },

        __setBackboneCollection(collection) {
            self.__collection = collection;
        }
    });

    const BaseStore = types
        .model(store.name, model)
        .volatile(volatile)
        .views(views)
        .actions(actions)
        .actions(asyncActions)
        .actions(backboneUtilityActions);

    return types.compose(BaseStore, store).named(store.name) as T;
};

export default BaseBackboneCollectionMigrationStore;
